import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { message, Form, Input, Button, Tooltip, DatePicker, Space, Select, Switch } from 'antd';
import { SendOutlined, ClockCircleOutlined, LockOutlined, FileOutlined, EyeOutlined, CloseOutlined, SaveOutlined, InfoCircleOutlined, EyeInvisibleOutlined, FileTextOutlined, PlusOutlined, CloudUploadOutlined, DownOutlined, UpOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons';
import axios from '../../utility/axios';
import { findComposeContact, normalizePhoneDigits } from '../../utility/composeContactHelpers';
import { useComposeContacts, filterContacts } from '../../hooks/useComposeContacts';
import ContactsDropdown from '../compose/ContactsDropdown';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import '../../styles/documents/DocumentCompose.css';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useSystemSettings } from '../../hooks/useSystemSettings';
import { useUserSettings } from '../../hooks/useUserSettings';
import MessageEditor from '../messages/MessageEditor';
import '../../styles/messages/MessageEditorDocument.css';
import { useTranslation } from 'react-i18next';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { ContactGroupModal } from '../settings';
import AccessCodeDeliveryModal from '../compose/AccessCodeDeliveryModal';

// Initialize dayjs plugins
dayjs.extend(relativeTime);


const DocumentCompose = ({ initialValues, isReply }) => {
    const { isLoggedIn, isLoading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { settings, loading: settingsLoading } = useSystemSettings();
    const { userSettings, loading: userSettingsLoading } = useUserSettings();
    const { t } = useTranslation();
    
    // Helper function to check if we're working with a template
    const isTemplate = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return !!urlParams.get('template_id');
    };
    const [submitLoading, setSubmitLoading] = useState(false);
    const [form] = Form.useForm();
    const [hasRecipients, setHasRecipients] = useState(false);
    // Add states for filtering contacts
    const [recipientSearchText, setRecipientSearchText] = useState('');

    // Add states for tag values - to avoid using form.getFieldValue directly in Select
    const [recipientTags, setRecipientTags] = useState([]);

    // Flag to prevent onChange validation when selecting a contact
    const [selectingContact, setSelectingContact] = useState(false);
    
    // Track dropdown state for each field
    const [recipientDropdownOpen, setRecipientDropdownOpen] = useState(false);
    
    // Update auto-save functionality states
    const [draftId, setDraftId] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedTime, setLastSavedTime] = useState(null);
    const [displayTimestamp, setDisplayTimestamp] = useState('');
    const autoSaveTimeoutRef = useRef(null);
    const initialLoadRef = useRef(true);
    const formModifiedRef = useRef(false);
    const updateDisplayTimestampRef = useRef(null);
    
    const [accessCodeDeliveryModalOpen, setAccessCodeDeliveryModalOpen] = useState(false);
    const [accessCodeDelivery, setAccessCodeDelivery] = useState({});
    const [accessCodeSmsPhones, setAccessCodeSmsPhones] = useState({});
    const [accessCodeBtnError, setAccessCodeBtnError] = useState(false);
    const contactPhoneSnapshotRef = useRef({});
    const [autoSaveEnabled, setAutoSaveEnabled] = useState(false);
    
    // Add state to track if we're loading a draft from localStorage
    const [loadingDraft, setLoadingDraft] = useState(false);
    const draftLoadedRef = useRef(false);
    
    // Add state for limited editing mode
    const [isLimitedEdit, setIsLimitedEdit] = useState(false);
    const [limitedEditMessageId, setLimitedEditMessageId] = useState(null);
    
    
    // Add state to track if this draft was originally a sent message
    const [isOriginallySentMessage, setIsOriginallySentMessage] = useState(false);
    
    // Add state to track if user came from sent folder (vs drafts folder)
    const [cameFromSentFolder, setCameFromSentFolder] = useState(false);
    
    // Add states for user settings widgets
    const [emailNotificationsEnabled, setEmailNotificationsEnabled] = useState(true);
    const [preventCopyEnabled, setPreventCopyEnabled] = useState(false);
    
    // Add state for document requests
    const [documentRequests, setDocumentRequests] = useState([]);
    const [documentRequestsError, setDocumentRequestsError] = useState(null);
    const [invalidDocumentRequestIds, setInvalidDocumentRequestIds] = useState(new Set());
    const [invalidDocumentRequestError, setInvalidDocumentRequestError] = useState(null);
    
    // Track which file type dropdown is open (closes on blur)
    const [openFileTypeDropdownId, setOpenFileTypeDropdownId] = useState(null);

    // Clear document requests error when user adds a request
    useEffect(() => {
        if (documentRequests.length > 0) {
            setDocumentRequestsError(null);
        }
    }, [documentRequests.length]);

    // Clear invalid document request error when all invalid cards are fixed
    useEffect(() => {
        if (invalidDocumentRequestIds.size === 0) {
            setInvalidDocumentRequestError(null);
        }
    }, [invalidDocumentRequestIds.size]);
    
    // Add state for document recents
    const [documentRecents, setDocumentRecents] = useState([]);
    const [documentRecentsLoading, setDocumentRecentsLoading] = useState(false);
    
    // Add state for template search
    const [templateSearchText, setTemplateSearchText] = useState('');
    
    // Add state for template sorting (default: most recent)
    const [templateSortType, setTemplateSortType] = useState('recent'); // 'recent' or 'used'
    
    // Add state for drag and drop visual feedback
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);
    const dragCounterRef = useRef(0);
    
    // Add state for template drag and drop
    const [isDraggingTemplate, setIsDraggingTemplate] = useState(false);
    const draggingTemplateRef = useRef(null);
    
    // Add state for collapsible form top section
    const [formTopCollapsed, setFormTopCollapsed] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    
    // Add state for templates panel collapsed (like MessageEditor attachments toggle)
    const [templatesCollapsed, setTemplatesCollapsed] = useState(false);
    
    // Templates panel width (percentage of requests column when expanded)
    const [templatesPanelWidth, setTemplatesPanelWidth] = useState(() => {
        const saved = localStorage.getItem('document_compose_templates_panel_width');
        return saved ? parseFloat(saved) : 32;
    });
    const [isResizingTemplates, setIsResizingTemplates] = useState(false);
    const requestsBodyRef = useRef(null);
    
    // Add state for resizable columns (percentages) - 2 columns: editor | requests, default 50/50
    const [columnWidths, setColumnWidths] = useState(() => {
        const saved = localStorage.getItem('document_compose_column_widths');
        const parsed = saved ? JSON.parse(saved) : null;
        if (parsed && typeof parsed.editor === 'number' && typeof parsed.requests === 'number') {
            return { editor: parsed.editor, requests: parsed.requests };
        }
        return { editor: 35, requests: 65 };
    });
    const [resizingColumn, setResizingColumn] = useState(null);
    const mainContentRef = useRef(null);
    
    // Handle column resize (editor | requests+templates)
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!resizingColumn || !mainContentRef.current) return;
            
            const containerRect = mainContentRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const mousePercent = (mouseX / containerWidth) * 100;
            
            let editorWidth = mousePercent;
            editorWidth = Math.max(30, Math.min(70, editorWidth));
            const newWidths = { editor: editorWidth, requests: 100 - editorWidth };
            setColumnWidths(newWidths);
            localStorage.setItem('document_compose_column_widths', JSON.stringify(newWidths));
        };

        const handleMouseUp = () => {
            if (resizingColumn) {
                setResizingColumn(null);
                document.body.classList.remove('document-compose-resizing-columns');
            }
        };

        if (resizingColumn) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('document-compose-resizing-columns');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('document-compose-resizing-columns');
        };
    }, [resizingColumn]);
    
    // Handle templates panel resize (between requests list and templates)
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isResizingTemplates || !requestsBodyRef.current) return;
            
            const containerRect = requestsBodyRef.current.getBoundingClientRect();
            const containerWidth = containerRect.width;
            const mouseX = e.clientX - containerRect.left;
            const percentFromRight = ((containerWidth - mouseX) / containerWidth) * 100;
            let newWidth = Math.max(20, Math.min(55, percentFromRight));
            setTemplatesPanelWidth(newWidth);
            localStorage.setItem('document_compose_templates_panel_width', newWidth.toString());
        };

        const handleMouseUp = () => {
            if (isResizingTemplates) {
                setIsResizingTemplates(false);
                document.body.classList.remove('document-compose-resizing-templates');
            }
        };

        if (isResizingTemplates) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.body.classList.add('document-compose-resizing-templates');
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.classList.remove('document-compose-resizing-templates');
        };
    }, [isResizingTemplates]);
    
    // Initialize user settings values
    useEffect(() => {
        if (userSettings) {
            setEmailNotificationsEnabled(userSettings.notification_settings?.email_notifications_enabled ?? true);
        }
    }, [userSettings]);

    useEffect(() => {
        if (!isLoggedIn) return;
        const fetchTemplates = async () => {
            setTemplatesLoading(true);
            try {
                const res = await axios.get('/api/document/templates/');
                setTemplates(res.data?.templates || []);
            } catch (err) {
                console.error('Failed to fetch templates:', err);
                setTemplates([]);
            } finally {
                setTemplatesLoading(false);
            }
        };
        fetchTemplates();
    }, [isLoggedIn]);
    
    // Check for limited edit mode
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const limitedEditId = urlParams.get('limited_edit');
        
        // Check where the user came from (sent folder vs drafts folder)
        const editSource = localStorage.getItem('editSource');
        if (editSource === 'sent') {
            setCameFromSentFolder(true);
        }
        
        if (limitedEditId) {
            // Get the limited edit data from localStorage
            const limitedEditData = JSON.parse(localStorage.getItem('limitedEditDocument') || '{}');
            
            if (limitedEditData && limitedEditData.limitedEdit) {
                setIsLimitedEdit(true);
                setLimitedEditMessageId(limitedEditData.documentId);
                
                // Use documentData from localStorage if available
                if (limitedEditData.documentData) {
                    const documentData = limitedEditData.documentData;
                    
                    // Parse recipients with better handling
                    const recipientArray = documentData.recipient 
                        ? documentData.recipient.split(',').map(email => email.trim()).filter(email => email !== '')
                        : [];
                        
                    // Update state variables first
                    setRecipientTags(recipientArray);
                    setHasRecipients(recipientArray.length > 0);
                    
                    // Load document files if they exist (read-only)
                    if (documentData.document_files && Array.isArray(documentData.document_files)) {
                        const loadedDocumentRequests = documentData.document_files.map(file => ({
                            id: file.unique_identifier || file.id || Date.now() + Math.random(),
                            title: file.title || '',
                            description: file.description || '',
                            return_type: file.return_type ? 
                                (typeof file.return_type === 'string' ? file.return_type.split(', ') : file.return_type) 
                                : ['*']
                        }));
                        setDocumentRequests(loadedDocumentRequests);
                    }
                    
                    // Set prevent copy toggle state from documentData
                    if (documentData.prevent_message_copy !== undefined && documentData.prevent_message_copy !== null) {
                        setPreventCopyEnabled(documentData.prevent_message_copy);
                    }
                    
                    // Need a slight delay to ensure state is updated before form values
                    setTimeout(() => {
                        // Set form values for all fields
                        form.setFieldsValue({
                            // Display-only fields
                            subject: documentData.subject || '',
                            message: documentData.body || '',
                            recipient: recipientArray,
                            
                            self_destruct: limitedEditData.self_destruct || false,
                            timestamp: limitedEditData.ttlive ? dayjs(limitedEditData.ttlive) : null,
                        });
                    }, 100);
                    
                    draftLoadedRef.current = true;
                } else {
                    // Fetch the complete document data to display in the form
                    const fetchDocumentDetails = async () => {
                        try {
                            const response = await axios.get(`/api/document/preview/${limitedEditData.documentId}/`);
                            const documentData = response.data.message;
                            
                            // Parse recipients with better handling
                            const recipientArray = documentData.recipient 
                                ? documentData.recipient.split(',').map(email => email.trim()).filter(email => email !== '')
                                : [];
                                
                            // Update state variables first
                            setRecipientTags(recipientArray);
                            setHasRecipients(recipientArray.length > 0);
                            
                            // Load document files if they exist (read-only)
                            if (documentData.document_files && Array.isArray(documentData.document_files)) {
                                const loadedDocumentRequests = documentData.document_files.map(file => ({
                                    id: file.unique_identifier || file.id || Date.now() + Math.random(),
                                    title: file.title || '',
                                    description: file.description || '',
                                    return_type: file.return_type ? 
                                        (typeof file.return_type === 'string' ? file.return_type.split(', ') : file.return_type) 
                                        : ['*']
                                }));
                                setDocumentRequests(loadedDocumentRequests);
                            }
                            
                            // Set prevent copy toggle state from documentData
                            if (documentData.prevent_message_copy !== undefined && documentData.prevent_message_copy !== null) {
                                setPreventCopyEnabled(documentData.prevent_message_copy);
                            }
                            
                            // Need a slight delay to ensure state is updated before form values
                            setTimeout(() => {
                                // Set form values for all fields
                                form.setFieldsValue({
                                    // Display-only fields
                                    subject: documentData.subject || '',
                                    message: documentData.body || '',
                                    recipient: recipientArray,
                                    
                                    self_destruct: limitedEditData.self_destruct || false,
                                    timestamp: limitedEditData.ttlive ? dayjs(limitedEditData.ttlive) : null,
                                });
                            }, 100);
                        } catch (error) {
                            console.error('Error fetching document details:', error);
                            message.error(t('documents.errors.failedToFetch'));
                        }
                    };
                    
                    fetchDocumentDetails();
                }
                
                // When submitting in limited edit mode, we'll use a different endpoint
                draftLoadedRef.current = true;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form]);
    
    // Load saved draft ID from localStorage
    useEffect(() => {
        if (!authLoading && isLoggedIn && !draftLoadedRef.current) {
            const loadDraftFromLocalStorage = async () => {
                try {
                    const urlParams = new URLSearchParams(window.location.search);
                    const draftIdFromUrl = urlParams.get('draft_id');
                    const templateIdFromUrl = urlParams.get('template_id');
                    
                    // Only use URL parameters or initialValues - do NOT fall back to localStorage
                    // This ensures the form stays blank when there's no draft_id or template_id in the URL
                    const draftIdToLoad = draftIdFromUrl || templateIdFromUrl || initialValues?.draft_id;
                    
                    // Only load a draft if we have an ID and haven't already loaded one
                    if (draftIdToLoad && !draftLoadedRef.current) {
                        setLoadingDraft(true);
                        
                        // Fetch the draft from backend
                        const draft = await fetchDraft(draftIdToLoad);
                        
                        if (draft) {
                            // Set the draft ID
                            setDraftId(draft.unique_identifier);
                            
                            // Parse recipients - use the recipient field directly from the draft
                            const recipientArray = draft.recipient 
                                ? draft.recipient.split(',').map(email => email.trim()).filter(email => email !== '')
                                : [];
                                
                            setRecipientTags(recipientArray);
                            setHasRecipients(recipientArray.length > 0);
                            
                            // Set expiry time
                            let expiryTime = null;
                            if (draft.ttlive) {
                                expiryTime = dayjs(draft.ttlive);
                            }
                            
                            // Set form values
                            form.setFieldsValue({
                                subject: draft.subject || '',
                                message: draft.body || '',
                                self_destruct: draft.self_destruct || false,
                                timestamp: expiryTime || null,
                                recipient: recipientArray
                            });
                            
                            // Set prevent copy toggle state
                            if (draft.prevent_message_copy !== undefined && draft.prevent_message_copy !== null) {
                                setPreventCopyEnabled(draft.prevent_message_copy);
                            }
                            
                            // Load document files if they exist
                            if (draft.document_files && Array.isArray(draft.document_files)) {
                                const loadedDocumentRequests = draft.document_files.map(file => ({
                                    id: file.unique_identifier || file.id || Date.now() + Math.random(),
                                    title: file.title || '',
                                    description: file.description || '',
                                    return_type: file.return_type ? 
                                        (typeof file.return_type === 'string' ? file.return_type.split(', ') : file.return_type) 
                                        : ['*']
                                }));
                                setDocumentRequests(loadedDocumentRequests);
                            }
                            
                            // Set last saved time
                            if (draft.updated_at) {
                                const savedTime = dayjs(draft.updated_at);
                                setLastSavedTime(savedTime);
                                updateDisplayTimestamp(savedTime);
                            }
                            
                            // ALWAYS enable auto-save when loading an existing draft
                            setAutoSaveEnabled(true);
                            console.log('Auto-save enabled for loaded draft:', draft.unique_identifier);
                            
                            draftLoadedRef.current = true;
                            
                            // Save to localStorage for persistence
                            localStorage.setItem('currentDraftId', draft.unique_identifier);
                        }
                        
                        setLoadingDraft(false);
                    }
                } catch (error) {
                    console.error('Error loading draft:', error);
                    setLoadingDraft(false);
                }
            };
            
            loadDraftFromLocalStorage();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authLoading, isLoggedIn, form, settings]);

    // Single useEffect to handle form initialization
    useEffect(() => {
        if (!settingsLoading && settings && !userSettingsLoading && userSettings && !draftLoadedRef.current) {
            // Use user settings for defaults - no fallback needed since defaults are set in database
            const messageLifetimeDays = userSettings.message_security?.message_lifetime_days;
            const dailyExpirationTime = userSettings.message_security?.daily_expiration_time;
            
            // Parse the daily expiration time from user settings
            const [hours, minutes] = dailyExpirationTime.split(':').map(Number);
            const defaultExpiry = dayjs()
                .add(messageLifetimeDays, 'days')
                .hour(hours)
                .minute(minutes)
                .second(0);

            // Process recipient to handle both string (comma-separated) and single values
            let recipientArray = [];

            // Check if initialValues contains a draft_id (for existing drafts passed as props)
            if (initialValues?.draft_id) {
                setDraftId(initialValues.draft_id);
                localStorage.setItem('currentDraftId', initialValues.draft_id);
                // Enable auto-save since this is an existing draft
                setAutoSaveEnabled(true);
                console.log('Auto-save enabled for initialValues draft:', initialValues.draft_id);
                draftLoadedRef.current = true;
            }

            // Process recipient emails
            if (initialValues?.recipient) {
                if (typeof initialValues.recipient === 'string') {
                    // Split by comma, semicolon, or space and filter out empty values
                    recipientArray = initialValues.recipient
                        .split(/[,;\s]+/)
                        .map(email => email.trim())
                        .filter(email => email !== '');
                } else if (initialValues.recipient) {
                    // Handle single value or array
                    const recipients = Array.isArray(initialValues.recipient) 
                        ? initialValues.recipient 
                        : [initialValues.recipient];
                    recipientArray = recipients.filter(email => email && typeof email === 'string' && email.trim() !== '');
                }
            }

            // Update has recipients state
            setHasRecipients(recipientArray.length > 0);
            setRecipientTags(recipientArray);  // Set the state-driven tags

            const defaultValues = {
                self_destruct: false,
                timestamp: defaultExpiry,
                ...initialValues,
                message: initialValues?.body || initialValues?.message || '', // Map body to message field
                recipient: recipientArray // Override with properly formatted recipient array
            };

            form.setFieldsValue(defaultValues);
            
            // Set prevent copy toggle state from initialValues
            if (initialValues?.prevent_message_copy !== undefined && initialValues?.prevent_message_copy !== null) {
                setPreventCopyEnabled(initialValues.prevent_message_copy);
            }
            
            // If initialValues had updated_at, set the last saved time
            if (initialValues?.updated_at) {
                const savedTime = dayjs(initialValues.updated_at);
                setLastSavedTime(savedTime);
                updateDisplayTimestamp(savedTime);
            }
            
            // After a slight delay, mark the form as initialized
            setTimeout(() => {
                initialLoadRef.current = false;
            }, 1000);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settingsLoading, settings, userSettingsLoading, userSettings, form, initialValues]);

    useEffect(() => {
        if (!authLoading && !isLoggedIn) {
            navigate('/login?redirect=/document-compose');
        }
    }, [isLoggedIn, authLoading, navigate]);

    const {
        contacts,
        contactsLoading,
        setContacts,
        fetchContacts,
        updateContactsApi,
        contactEditModalVisible,
        contactEditingContact,
        handleContactAddFavorite,
        handleContactRemoveFavorite,
        handleContactEdit,
        handleContactDelete,
        handleContactEditModalClose,
        handleContactEditModalSaved,
    } = useComposeContacts({ apiEndpoint: '/api/compose/contacts/', isLoggedIn, authLoading, t });

    // Add function to fetch document recents
    const fetchDocumentRecents = async () => {
        if (!isLoggedIn) return;
        
        try {
            setDocumentRecentsLoading(true);
            const response = await axios.get('/api/get_document_recents/');
            setDocumentRecents(response.data.document_recents || []);
        } catch (error) {
            console.error('Error fetching document recents:', error);
        } finally {
            setDocumentRecentsLoading(false);
        }
    };

    useEffect(() => {
        if (!authLoading && isLoggedIn) {
            fetchDocumentRecents();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn, authLoading]);

    useEffect(() => {
        const all = recipientTags.map((e) => String(e).trim()).filter(Boolean);
        setAccessCodeSmsPhones((prev) => {
            const next = { ...prev };
            all.forEach((email) => {
                if (next[email] === undefined) {
                    const c = findComposeContact(contacts, email);
                    if (c?.phone) next[email] = c.phone;
                }
            });
            return next;
        });
    }, [recipientTags, contacts]);

    useEffect(() => {
        if (!accessCodeDeliveryModalOpen) return;
        const all = recipientTags.map((e) => String(e).trim()).filter(Boolean);
        const snap = {};
        all.forEach((email) => {
            const c = findComposeContact(contacts, email);
            if (c) snap[email] = c.phone || '';
        });
        contactPhoneSnapshotRef.current = snap;
        setAccessCodeSmsPhones((prev) => {
            const next = { ...prev };
            all.forEach((email) => {
                if (next[email] === undefined && snap[email]) {
                    next[email] = snap[email];
                }
            });
            return next;
        });
    }, [accessCodeDeliveryModalOpen, recipientTags, contacts]);

    const filteredRecipientContacts = useMemo(() =>
        filterContacts(contacts, recipientSearchText),
        [contacts, recipientSearchText]
    );

    // Function to filter and sort document recents/templates
    const filterAndSortTemplates = (templates, searchText, sortType) => {
        if (!templates || templates.length === 0) return [];
        
        // Apply search filter first
        let filtered = templates;
        if (searchText && searchText.trim() !== '') {
            const lowerCaseSearch = searchText.toLowerCase();
            filtered = templates.filter(template => {
                if (!template) return false;
                const title = (template.title || '').toLowerCase();
                const description = (template.description || '').toLowerCase();
                return title.includes(lowerCaseSearch) || description.includes(lowerCaseSearch);
            });
        }
        
        // Apply sorting based on type
        return [...filtered].sort((a, b) => {
            if (searchText && searchText.trim() !== '') {
                // When searching, prioritize title matches first
                const aTitle = (a.title || '').toLowerCase();
                const bTitle = (b.title || '').toLowerCase();
                const lowerCaseSearch = searchText.toLowerCase();
                const aTitleMatch = aTitle.includes(lowerCaseSearch);
                const bTitleMatch = bTitle.includes(lowerCaseSearch);
                
                if (aTitleMatch && !bTitleMatch) return -1;
                if (!aTitleMatch && bTitleMatch) return 1;
            }
            
            // Apply selected sort type
            if (sortType === 'used') {
                // Sort by usage count (most used first)
                return (b.numberused || 0) - (a.numberused || 0);
            } else {
                // Sort by most recent (assuming we have a created_at or updated_at field)
                // If no date field available, fall back to ID (higher ID = more recent)
                const aDate = new Date(a.updated_at || a.created_at || 0);
                const bDate = new Date(b.updated_at || b.created_at || 0);
                if (aDate.getTime() !== bDate.getTime()) {
                    return bDate.getTime() - aDate.getTime();
                }
                // Fallback to ID if dates are the same or missing
                return (b.id || 0) - (a.id || 0);
            }
        });
    };
    
    // Memoized filtered and sorted templates
    const filteredTemplates = useMemo(() => 
        filterAndSortTemplates(documentRecents, templateSearchText, templateSortType), 
        [documentRecents, templateSearchText, templateSortType]
    );

    // Function to toggle sort type
    const handleSortToggle = () => {
        setTemplateSortType(prev => prev === 'recent' ? 'used' : 'recent');
    };

    // Auto-save functionality
    const saveDraft = useCallback(async () => {
        if (!formModifiedRef.current || initialLoadRef.current) {
            return;
        }
        
        try {
            setIsSaving(true);
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            // Prepare data for sending
            const params = new URLSearchParams();
            if (draftId) {
                params.append('draft_id', draftId);
            }
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            
            // Always include recipient even if empty
            params.append('recipient', recipientValue);
            
            params.append('self_destruct', formValues.self_destruct || false);
            
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Include document requests as JSON
            console.log('Document requestsTEST2:', documentRequests.length);
            if (documentRequests.length > 0) {
                const formattedDocumentRequests = documentRequests.map(request => ({
                    ...request,
                    return_type: Array.isArray(request.return_type) 
                        ? request.return_type.join(', ') 
                        : request.return_type
                }));
                params.append('document_requests', JSON.stringify(formattedDocumentRequests));
                console.log('Document requestsTEST:', formattedDocumentRequests);
            }
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            // If we're editing a template, mark it as template
            if (isTemplate()) {
                params.append('is_template', 'true');
            }
            
            const response = await axios.post('/api/save_document_draft/', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            // Update draftId if it's a new draft
            if (!draftId && response.data.draft_id) {
                const newDraftId = response.data.draft_id;
                setDraftId(newDraftId);
                
                // Save to localStorage
                localStorage.setItem('currentDraftId', newDraftId);
            }
            
            // Update the last saved time
            if (response.data.updated_at) {
                const savedTime = dayjs(response.data.updated_at);
                setLastSavedTime(savedTime);
                updateDisplayTimestamp(savedTime);
            }
            
            // Keep the saving indicator visible for 3 seconds
            setTimeout(() => {
                setIsSaving(false);
            }, 3000);
            
            // Reset the modified flag since we just saved
            formModifiedRef.current = false;
        } catch (error) {
            console.error('Error saving draft:', error);
            setIsSaving(false);
            // Use backend error message if available, otherwise fall back to generic message
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error || 
                                error.response?.data?.message ||
                                t('messages.errors.saveFailed');
            message.error(errorMessage);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form, draftId, emailNotificationsEnabled, preventCopyEnabled, documentRequests]);
    
    // Function to update the display timestamp
    const updateDisplayTimestamp = useCallback((savedTime) => {
        if (!savedTime) return;
        
        const now = dayjs();
        const timeAgo = savedTime.from(now);
        setDisplayTimestamp(timeAgo);
    }, []);
    
    // Set up an interval to update the "time ago" display
    useEffect(() => {
        if (lastSavedTime) {
            // Update the timestamp display immediately
            updateDisplayTimestamp(lastSavedTime);
            
            // Set up an interval to update the timestamp every minute
            updateDisplayTimestampRef.current = setInterval(() => {
                updateDisplayTimestamp(lastSavedTime);
            }, 60000); // Update every minute
        }
        
        return () => {
            if (updateDisplayTimestampRef.current) {
                clearInterval(updateDisplayTimestampRef.current);
            }
        };
    }, [lastSavedTime, updateDisplayTimestamp]);
    
    const triggerAutoSave = useCallback(() => {
        // Only trigger auto-save if it's enabled AND we're not editing a template
        if (!autoSaveEnabled || isTemplate()) {
            console.log('Auto-save triggered but not enabled or editing template, skipping');
            return;
        }
        
        console.log('Auto-save triggered, setting timeout for 5 seconds');
        
        // Clear any existing timeout
        if (autoSaveTimeoutRef.current) {
            clearTimeout(autoSaveTimeoutRef.current);
        }
        
        // Set a new timeout to save after 5 seconds of inactivity
        autoSaveTimeoutRef.current = setTimeout(() => {
            console.log('Auto-save timeout reached, saving draft');
            saveDraft();
        }, 5000);
    }, [saveDraft, autoSaveEnabled]);
    
    // Set up form field change monitoring for auto-save
    useEffect(() => {
        // Initial load of form values
        form.getFieldsValue();
        
        // We're not using form.subscribe since it doesn't exist
        // Instead, we're relying on:
        // 1. onValuesChange prop on the Form component
        // 2. User activity event listeners (keydown, click, input, change)
        
        // Clear timeout on unmount
        return () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
            }
        };
    }, [form, triggerAutoSave]);
    
    // After initial form data is loaded, mark initialLoad as complete
    useEffect(() => {
        if (!settingsLoading && settings && initialLoadRef.current) {
            // After form is initialized, mark initialLoad as complete
            setTimeout(() => {
                initialLoadRef.current = false;
            }, 1000);
        }
    }, [settingsLoading, settings]);

    // Add an effect to monitor user activity across form fields
    useEffect(() => {
        const resetAutoSaveTimeout = () => {
            if (autoSaveTimeoutRef.current) {
                clearTimeout(autoSaveTimeoutRef.current);
                autoSaveTimeoutRef.current = setTimeout(() => {
                    saveDraft();
                }, 5000);
            }
        };
        
        // Add event listeners to capture user activity
        const formElement = document.querySelector('.document-compose-detail-body-wrapper form');
        if (formElement) {
            formElement.addEventListener('keydown', resetAutoSaveTimeout);
            formElement.addEventListener('click', resetAutoSaveTimeout);
            formElement.addEventListener('input', resetAutoSaveTimeout);
            formElement.addEventListener('change', resetAutoSaveTimeout);
        }
        
        return () => {
            if (formElement) {
                formElement.removeEventListener('keydown', resetAutoSaveTimeout);
                formElement.removeEventListener('click', resetAutoSaveTimeout);
                formElement.removeEventListener('input', resetAutoSaveTimeout);
                formElement.removeEventListener('change', resetAutoSaveTimeout);
            }
        };
    }, [saveDraft]);

    // Drag and drop handlers - show feedback that attachments aren't supported for document requests
    const handleDragEnter = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        // Only respond to file drags, not other drags (like reordering document requests)
        if (e.dataTransfer.types && e.dataTransfer.types.includes('Files')) {
            dragCounterRef.current++;
            if (dragCounterRef.current === 1) {
                setIsDraggingFiles(true);
            }
        }
    }, []);

    const handleDragLeave = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        dragCounterRef.current--;
        if (dragCounterRef.current === 0) {
            setIsDraggingFiles(false);
        }
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
    }, []);

    const handleFileDrop = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        
        dragCounterRef.current = 0;
        setIsDraggingFiles(false);
        
        // Show message that file attachments aren't available for document requests
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            message.info(t('documents.send.attachmentsNotAvailable'));
        }
    }, [t]);

    if (authLoading || !isLoggedIn) return null;

    // Add a function to fetch a draft by ID
    const fetchDraft = async (draftId) => {
        try {
            // Check if this is a template by looking at URL parameters
            const urlParams = new URLSearchParams(window.location.search);
            const isTemplate = urlParams.get('template_id') || urlParams.get('folder') === 'templates';
            
            // Use appropriate endpoint based on whether it's a template or draft
            const endpoint = isTemplate ? `/api/get_document_template/${draftId}/` : `/api/get_document_draft/${draftId}/`;
            const response = await axios.get(endpoint);
            
            // Check if this draft has recipients (indicating it was originally a sent message)
            // True drafts from the drafts table won't have message_receivers, but sent messages moved to drafts will
            const draft = response.data.draft;
            if (draft && draft.recipient) {
                setIsOriginallySentMessage(true);
            }
            
            return draft;
        } catch (error) {
            console.error('Error fetching draft:', error);
            return null;
        }
    };

    // Handler for contact selection
    const handleContactSelect = (email, field, isGroup = false) => {
        // Set flag to prevent onChange validation - set it before we modify any state
        setSelectingContact(true);
        
        // Get current values from state
        let currentValues = [];
        
        if (field === 'recipient') {
            currentValues = [...recipientTags];
            setRecipientSearchText('');
        }

        const wasEmpty = currentValues.length === 0;

        // If it's a group, split the email string by semicolon to get individual emails
        const emailsToProcess = isGroup 
            ? email.split(';').map(e => e.trim()).filter(e => e !== '')
            : [email];
        
        // Check if ALL emails from the group are already in currentValues
        const allEmailsExist = emailsToProcess.every(e => currentValues.includes(e));
        
        if (allEmailsExist) {
            // If all emails exist, remove them all (toggle off)
            emailsToProcess.forEach(e => {
                const index = currentValues.indexOf(e);
                if (index >= 0) {
                    currentValues.splice(index, 1);
                }
            });
        } else {
            // If not all emails exist, add all missing ones
            emailsToProcess.forEach(e => {
                if (!currentValues.includes(e)) {
                    currentValues.push(e);
                }
            });
        }
        
        // Use setTimeout to ensure state updates are processed before form updates
        setTimeout(() => {
            // Update form directly and sync with state
            form.setFieldsValue({ [field]: currentValues });
            
            // Update recipient-specific state
            if (field === 'recipient') {
                setRecipientTags(currentValues);
                setHasRecipients(currentValues.length > 0);
            }
            
            // Mark form as modified to trigger auto-save
            formModifiedRef.current = true;
            triggerAutoSave();

            // Close dropdown after first contact added; keep open for subsequent picks
            if (wasEmpty && field === 'recipient') setRecipientDropdownOpen(false);

            // Reset flag after onChange has completed
            setTimeout(() => {
                setSelectingContact(false);
            }, 100);
        }, 10);
    };

    const handleAccessCodeModalSave = async () => {
        const all = recipientTags.map((e) => String(e).trim()).filter(Boolean);
        const snap = contactPhoneSnapshotRef.current || {};
        for (const email of all) {
            const c = findComposeContact(contacts, email);
            if (!c?.id) continue;
            const method = accessCodeDelivery[email] ?? 'sms';
            if (method !== 'sms') continue;
            const current = accessCodeSmsPhones[email] ?? '';
            const before = snap[email] ?? '';
            if (normalizePhoneDigits(current) === normalizePhoneDigits(before)) continue;
            try {
                await updateContactsApi({
                    ids: [c.id],
                    action: 'update',
                    email: c.email,
                    firstname: c.firstname || '',
                    lastname: c.lastname || '',
                    phone: current,
                });
                setContacts((prev) =>
                    prev.map((x) => (x._type === 'contact' && x.id === c.id ? { ...x, phone: current } : x))
                );
            } catch (err) {
                console.error(err);
                message.error(t('compose.accessCodeDelivery.contactUpdateFailed'));
                return;
            }
        }
        setAccessCodeDeliveryModalOpen(false);
    };

    // Process email pattern validation
    const validateEmail = (email) => {
        if (!email || typeof email !== 'string') {
            return false;
        }
        
        // Trim any whitespace
        const trimmedEmail = email.trim();
        if (trimmedEmail === '') {
            return false;
        }
        
        // Check for basic email format - must have @ with something before and after, and a domain with at least one period
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
        return isValid;
    };

    // Add a dedicated function for limited editing
    const handleLimitedEdit = async (values) => {
        setSubmitLoading(true);
        try {
            // Use URLSearchParams for the limited edit request
            const params = new URLSearchParams();
            
            params.append('subject', values.subject || '');
            params.append('self_destruct', values.self_destruct || false);
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            if (values.timestamp) {
                params.append('timestamp', values.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Send the update request to the document update endpoint
            await axios.post(`/api/document/update/${limitedEditMessageId}/`, params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            // Clear localStorage and limited edit state
            localStorage.removeItem('limitedEditDocument');
            
            // Show success message
            message.success(t('documents.send.documentUpdated'));
            
            // Navigate back to the mailbox
            navigate('/profile/mailbox');
        } catch (error) {
            console.error('Error updating document:', error);
            // Use backend error message if available, otherwise fall back to generic message
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error || 
                                error.response?.data?.message ||
                                t('documents.errors.updateFailed');
            message.error(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        if (isLimitedEdit) {
            return handleLimitedEdit(values);
        }
        const allEmails = (recipientTags || []).map((e) => String(e).trim()).filter(Boolean);
        const missingSmsPhone = allEmails.some(
            (email) => (accessCodeDelivery[email] ?? 'sms') === 'sms' && !accessCodeSmsPhones[email]?.trim()
        );
        if (missingSmsPhone) {
            message.error(t('compose.accessCodeDelivery.smsPhoneMissing'));
            setAccessCodeBtnError(true);
            setTimeout(() => setAccessCodeBtnError(false), 1000);
            return;
        }
        if (documentRequests.length === 0) {
            setDocumentRequestsError(t('documents.errors.mustRequestOne'));
            return;
        }
        setDocumentRequestsError(null);
        const invalidIds = documentRequests
            .filter((r) => !(r.title || '').trim() || !(r.description || '').trim())
            .map((r) => r.id);
        if (invalidIds.length > 0) {
            setInvalidDocumentRequestIds(new Set(invalidIds));
            setInvalidDocumentRequestError(t('documents.errors.missingDocumentRequestFields'));
            return;
        }
        setSubmitLoading(true);
        try {
            // Get the recipient string - ensure proper handling of all formats
            let recipientValue = '';
            
            // Handle array of recipients
            if (values.recipient && Array.isArray(values.recipient) && values.recipient.length > 0) {
                // Join with comma and space
                recipientValue = values.recipient.join(', ');
            } 
            // Handle single string recipient
            else if (values.recipient && typeof values.recipient === 'string' && values.recipient.trim() !== '') {
                recipientValue = values.recipient.trim();
            }
            
            // Use URLSearchParams for simple x-www-form-urlencoded data
            const params = new URLSearchParams();
            params.append('subject', values.subject || '');
            params.append('message', values.message || '');
            params.append('self_destruct', values.self_destruct || false);
            params.append('recipient', recipientValue);
            
            if (values.timestamp) {
                params.append('timestamp', values.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Include document requests as JSON
            if (documentRequests.length > 0) {
                // Format document requests with comma-separated return types
                const formattedDocumentRequests = documentRequests.map(request => ({
                    ...request,
                    return_type: Array.isArray(request.return_type) 
                        ? request.return_type.join(', ') 
                        : request.return_type
                }));
                params.append('document_requests', JSON.stringify(formattedDocumentRequests));
            }
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            // Check if we're working with a template
            const urlParams = new URLSearchParams(window.location.search);
            const isEditingTemplate = !!urlParams.get('template_id');
            
            if (draftId && !isEditingTemplate) {
                params.append('draft_id', draftId);
            }

            const allEmails = recipientValue ? recipientValue.split(/[,;\s]+/).map((e) => e.trim()).filter(Boolean) : [];
            const deliveryToSend = {};
            const phonesToSend = {};
            allEmails.forEach((email) => {
                deliveryToSend[email] = accessCodeDelivery[email] ?? 'sms';
                if (deliveryToSend[email] === 'sms' && accessCodeSmsPhones[email]) {
                    phonesToSend[email] = accessCodeSmsPhones[email];
                }
            });
            params.append('access_code_delivery', JSON.stringify(deliveryToSend));
            if (Object.keys(phonesToSend).length > 0) {
                params.append('access_code_sms_phones', JSON.stringify(phonesToSend));
            }

            // Send the request without attachments
            const response = await axios.post('/api/send_doc_message/', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            // Store unique identifier before resetting
            const uniqueIdentifier = response.data.unique_identifier;
            
            // Clear localStorage draft ID
            localStorage.removeItem('currentDraftId');
            localStorage.removeItem('editSource');
            
            // If we were editing a draft, we should delete the draft from the server since it's now sent
            // But don't delete if we're working with a template (template should remain intact)
            if (draftId && !isEditingTemplate) {
                try {
                    await axios.delete(`/api/delete_document_draft/${draftId}/`);
                } catch (error) {
                    console.error('Error deleting draft after sending:', error);
                    // Non-critical error, we can continue
                }
            }
            
            // Reset form
            form.resetFields();
            setRecipientTags([]);
            setHasRecipients(false);
            setDraftId(null);
            setDocumentRequests([]); // Clear document requests
            formModifiedRef.current = false;
            
            navigate('/profile/mailbox');
        } catch (error) {
            console.error('Error sending message:', error);
            // Use backend error message if available, otherwise fall back to generic message
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error || 
                                error.response?.data?.message ||
                                t('messages.errors.sendFailed');
            message.error(errorMessage);
        } finally {
            setSubmitLoading(false);
        }
    };


    // Add function to directly add a document request
    const handleDocumentRequestClick = () => {
        // Create a new document request with empty values to show placeholders
        const newDocumentRequest = {
            id: Date.now(), // Use timestamp as temporary ID
            title: '', // Empty to show placeholder
            description: '', // Empty to show placeholder
            return_type: ['*'] // Default to any file type
        };
        
        // Add the document request to the state at the top of the list
        setDocumentRequests(prev => [newDocumentRequest, ...prev]);
    };

    // Handle drag and drop for reordering requests
    const handleDragEnd = (result) => {
        const { source, destination } = result;

        // Dropped outside a valid droppable
        if (!destination) {
            return;
        }

        // Handle reordering within requests
        if (source.droppableId === 'requests' && destination.droppableId === 'requests') {
            // No change in position
            if (source.index === destination.index) {
                return;
            }

            const newRequests = Array.from(documentRequests);
            const [removed] = newRequests.splice(source.index, 1);
            newRequests.splice(destination.index, 0, removed);
            
            setDocumentRequests(newRequests);
            
            // Mark form as modified to trigger auto-save
            formModifiedRef.current = true;
            triggerAutoSave();
        }
    };


    // Add function to remove document request
    const handleRemoveDocumentRequest = (requestId) => {
        setDocumentRequests(prev => prev.filter(request => request.id !== requestId));
    };

    // Add function to handle direct field updates (auto-save)
    const handleDocumentRequestFieldChange = (requestId, field, value) => {
        setDocumentRequests(prev => prev.map(request => 
            request.id === requestId 
                ? { ...request, [field]: value }
                : request
        ));
        if (field === 'title' || field === 'description') {
            setInvalidDocumentRequestIds(prev => {
                const next = new Set(prev);
                next.delete(requestId);
                return next;
            });
        }
        // Mark form as modified to trigger auto-save
        formModifiedRef.current = true;
        triggerAutoSave();
    };

    // Template drag and drop handlers
    const handleTemplateDragStart = (e, template) => {
        draggingTemplateRef.current = template;
        setIsDraggingTemplate(true);
        e.dataTransfer.effectAllowed = 'copy';
        e.dataTransfer.setData('text/plain', JSON.stringify(template));
        // Add a custom drag image
        const dragEl = e.currentTarget.cloneNode(true);
        dragEl.style.position = 'absolute';
        dragEl.style.top = '-1000px';
        dragEl.style.opacity = '0.8';
        dragEl.style.transform = 'rotate(2deg)';
        document.body.appendChild(dragEl);
        e.dataTransfer.setDragImage(dragEl, 20, 20);
        setTimeout(() => document.body.removeChild(dragEl), 0);
    };

    const handleTemplateDragEnd = () => {
        draggingTemplateRef.current = null;
        setIsDraggingTemplate(false);
    };

    const handleRequestsDragOver = (e) => {
        if (draggingTemplateRef.current) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleRequestsDrop = (e) => {
        e.preventDefault();
        if (draggingTemplateRef.current) {
            handleUseDocumentRecent(draggingTemplateRef.current);
            draggingTemplateRef.current = null;
            setIsDraggingTemplate(false);
        }
    };

    // Add functions for document recents
    const handleUseDocumentRecent = (recent) => {
        // Create a new document request based on the recent
        const newDocumentRequest = {
            id: Date.now(),
            title: recent.title,
            description: recent.description,
            return_type: recent.return_type ? recent.return_type.split(', ') : ['*']
        };
        
        // Add to document requests at the top of the list
        setDocumentRequests(prev => [newDocumentRequest, ...prev]);
        
        // Update usage count
        const updatedRecent = { ...recent, numberused: recent.numberused + 1 };
        handleUpdateDocumentRecent(recent.id, updatedRecent);
        
        message.success(t('documents.send.addedTemplate', { title: recent.title }));
    };

    const handleUpdateDocumentRecent = async (recentId, updatedData) => {
        try {
            const params = new URLSearchParams();
            params.append('title', updatedData.title);
            params.append('description', updatedData.description);
            params.append('return_type', Array.isArray(updatedData.return_type) ? updatedData.return_type.join(', ') : updatedData.return_type);
            params.append('numberused', updatedData.numberused);

            await axios.post(`/api/update_document_recent/${recentId}/`, params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            // Refresh the document recents list
            fetchDocumentRecents();
        } catch (error) {
            console.error('Error updating document recent:', error);
        }
    };

    const handleDeleteDocumentRecent = async (recentId) => {
        try {
            await axios.delete(`/api/delete_document_recent/${recentId}/`);
            
            // Refresh the document recents list
            fetchDocumentRecents();
            message.success(t('documents.send.templateDeletedSuccess'));
        } catch (error) {
            console.error('Error deleting document recent:', error);
            message.error(t('documents.send.templateDeleteFailed'));
        }
    };


    // Add function to handle "Save as Template" button click
    const handleSaveAsTemplate = async () => {
        try {
            // Mark as modified to ensure it saves
            formModifiedRef.current = true;
            
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            // Prepare data for sending
            const params = new URLSearchParams();
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            params.append('recipient', recipientValue);
            params.append('is_template', 'true');
            
            params.append('self_destruct', formValues.self_destruct || false);
            
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Include document requests as JSON
            if (documentRequests.length > 0) {
                const formattedDocumentRequests = documentRequests.map(request => ({
                    ...request,
                    return_type: Array.isArray(request.return_type) 
                        ? request.return_type.join(', ') 
                        : request.return_type
                }));
                params.append('document_requests', JSON.stringify(formattedDocumentRequests));
            }
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            await axios.post('/api/save_document_draft/', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            message.success(t('documents.send.templateSavedSuccess'));
            
        } catch (error) {
            console.error('Error saving document template:', error);
            // Use backend error message if available, otherwise fall back to generic message
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error || 
                                error.response?.data?.message ||
                                'Failed to save document template';
            message.error(errorMessage);
        }
    };

    // Add function to handle "Save as Draft" button click
    const handleSaveAsDraft = async () => {
        try {
            // Mark as modified to ensure it saves
            formModifiedRef.current = true;
            
            // If we're editing a template, always create a new draft (don't update the template)
            const urlParams = new URLSearchParams(window.location.search);
            const isEditingTemplate = !!urlParams.get('template_id');
            
            if (isEditingTemplate) {
                // Create a new draft from template data - don't pass draft_id to avoid updating template
                const formValues = form.getFieldsValue();
                
                // Process recipients
                let recipientValue = '';
                
                if (formValues.recipient && Array.isArray(formValues.recipient)) {
                    recipientValue = formValues.recipient.join(', ');
                } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                    recipientValue = formValues.recipient.trim();
                }
                
                // Prepare data for sending - NO draft_id to create new draft
                const params = new URLSearchParams();
                params.append('subject', formValues.subject || '');
                params.append('message', formValues.message || '');
                params.append('recipient', recipientValue);
                
                params.append('self_destruct', formValues.self_destruct || false);
                
                if (formValues.timestamp) {
                    params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
                }
                
                
                // Include document requests as JSON
                if (documentRequests.length > 0) {
                    const formattedDocumentRequests = documentRequests.map(request => ({
                        ...request,
                        return_type: Array.isArray(request.return_type) 
                            ? request.return_type.join(', ') 
                            : request.return_type
                    }));
                    params.append('document_requests', JSON.stringify(formattedDocumentRequests));
                }
                
                // Include email notifications setting
                params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
                
                // Include prevent copy setting
                params.append('prevent_message_copy', preventCopyEnabled.toString());
                
                const response = await axios.post('/api/save_document_draft/', params, {
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
                });
                
                // Update to the new draft ID and enable auto-save
                if (response.data.draft_id) {
                    setDraftId(response.data.draft_id);
                    localStorage.setItem('currentDraftId', response.data.draft_id);
                    setAutoSaveEnabled(true);
                    
                    // Update URL to reflect we're now editing a draft, not a template
                    const newUrl = `/document-compose?draft_id=${response.data.draft_id}`;
                    window.history.replaceState({}, '', newUrl);
                }
                
                message.success('Document draft created from template');
            } else {
                // Normal draft save/update logic
                const isUpdating = draftId && autoSaveEnabled;
                console.log('Save as Draft clicked. Is updating:', isUpdating, 'Draft ID:', draftId, 'Auto-save enabled:', autoSaveEnabled);
                
                // Enable auto-save for future changes if not already enabled
                if (!autoSaveEnabled) {
                    setAutoSaveEnabled(true);
                    console.log('Auto-save enabled via Save as Draft button');
                }
                
                // Save immediately
                await saveDraft();
            }
            
            // Draft status indicators provide feedback - no popup needed
        } catch (error) {
            console.error('Error saving document draft:', error);
            message.error('Failed to save document draft');
        }
    };

    // Add function to handle "Update Template" button click
    const handleUpdateTemplate = async () => {
        try {
            setIsSaving(true);
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            // Prepare data for sending
            const params = new URLSearchParams();
            params.append('draft_id', draftId);
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            params.append('recipient', recipientValue);
            params.append('is_template', 'true');
            
            params.append('self_destruct', formValues.self_destruct || false);
            
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Include document requests as JSON
            if (documentRequests.length > 0) {
                const formattedDocumentRequests = documentRequests.map(request => ({
                    ...request,
                    return_type: Array.isArray(request.return_type) 
                        ? request.return_type.join(', ') 
                        : request.return_type
                }));
                params.append('document_requests', JSON.stringify(formattedDocumentRequests));
            }
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            const response = await axios.post('/api/save_document_draft/', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            // Update the last saved time
            if (response.data.updated_at) {
                const savedTime = dayjs(response.data.updated_at);
                setLastSavedTime(savedTime);
                updateDisplayTimestamp(savedTime);
            }
            
            // Keep the saving indicator visible for 3 seconds
            setTimeout(() => {
                setIsSaving(false);
            }, 3000);
            
            // Reset the modified flag since we just saved
            formModifiedRef.current = false;
            
        } catch (error) {
            console.error('Error updating document template:', error);
            setIsSaving(false);
            // Use backend error message if available, otherwise fall back to generic message
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error || 
                                error.response?.data?.message ||
                                'Failed to update document template';
            message.error(errorMessage);
        }
    };

    // Add function to handle "Save as Draft" button click (creates new draft from template)
    const handleSaveAsDraftFromTemplate = async () => {
        try {
            // Mark as modified to ensure it saves
            formModifiedRef.current = true;
            
            const formValues = form.getFieldsValue();
            
            // Process recipients
            let recipientValue = '';
            
            if (formValues.recipient && Array.isArray(formValues.recipient)) {
                recipientValue = formValues.recipient.join(', ');
            } else if (formValues.recipient && typeof formValues.recipient === 'string') {
                recipientValue = formValues.recipient.trim();
            }
            
            // Prepare data for sending - NO draft_id to create new draft, NO is_template
            const params = new URLSearchParams();
            params.append('subject', formValues.subject || '');
            params.append('message', formValues.message || '');
            params.append('recipient', recipientValue);
            
            params.append('self_destruct', formValues.self_destruct || false);
            
            if (formValues.timestamp) {
                params.append('timestamp', formValues.timestamp.format('YYYY-MM-DD HH:mm:ss'));
            }
            
            
            // Include document requests as JSON
            if (documentRequests.length > 0) {
                const formattedDocumentRequests = documentRequests.map(request => ({
                    ...request,
                    return_type: Array.isArray(request.return_type) 
                        ? request.return_type.join(', ') 
                        : request.return_type
                }));
                params.append('document_requests', JSON.stringify(formattedDocumentRequests));
            }
            
            // Include email notifications setting
            params.append('email_notifications_enabled', emailNotificationsEnabled.toString());
            
            // Include prevent copy setting
            params.append('prevent_message_copy', preventCopyEnabled.toString());
            
            await axios.post('/api/save_document_draft/', params, {
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
            });
            
            message.success(t('documents.send.draftSavedAsSuccess'));
            
        } catch (error) {
            console.error('Error saving document draft from template:', error);
            // Use backend error message if available, otherwise fall back to generic message
            const errorMessage = error.response?.data?.status || 
                                error.response?.data?.error || 
                                error.response?.data?.message ||
                                'Failed to save document draft';
            message.error(errorMessage);
        }
    };

    // Add function to handle cancel with special logic for originally sent messages
    const handleCancel = async () => {
        try {
            // Only revert to inbox if user came from sent folder AND it's an originally sent message AND it's currently in drafts
            // Since messages now only move to drafts when saved, we need to check if it was actually moved
            if (cameFromSentFolder && isOriginallySentMessage && draftId) {
                // Check if the message is currently in drafts folder before reverting
                try {
                    await axios.get(`/api/get_document_draft/${draftId}/`);
                    // If we can get it as a draft or it's an email in drafts folder, then revert it
                    await axios.post(`/api/profile/mailbox/revert/${draftId}/`);
                } catch (error) {
                    // If the draft/email doesn't exist or isn't in drafts, don't try to revert
                    console.log('No need to revert - message was not moved to drafts');
                }
            }
            
            // Clear localStorage draft ID and edit source
            localStorage.removeItem('currentDraftId');
            localStorage.removeItem('editSource');
            
            // Navigate back to mailbox
            navigate('/profile/mailbox');
        } catch (error) {
            console.error('Error reverting message:', error);
            // Still navigate even if revert fails
            localStorage.removeItem('editSource');
            navigate('/profile/mailbox');
        }
    };

    return (
        <>
        <div 
            className={`document-compose-container ${isDraggingFiles ? 'dragging-files' : ''}`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleFileDrop}
        >
            {/* Drag and Drop Overlay - Document requests don't support file attachments */}
            {isDraggingFiles && (
                <div className="document-compose-drop-overlay">
                    <div className="document-compose-drop-content document-compose-drop-info">
                        <CloudUploadOutlined className="document-compose-drop-icon" />
                        <h2 className="document-compose-drop-title">{t('documents.send.dropFilesNotSupported')}</h2>
                        <p className="document-compose-drop-subtitle">{t('documents.send.dropFilesNotSupportedSubtitle')}</p>
                    </div>
                </div>
            )}
            {/* Top section - matches MessagePreview / TTLSend */}
            <div className="document-compose-top-header">
                <div className="document-compose-header">
                        <div className="document-compose-header-left">
                            {/* Draft Status Indicator */}
                            {isSaving && !isLimitedEdit && (
                                <div className="document-compose-draft-status-indicator saving">
                                    <SaveOutlined /> 
                                    <span>
                                        {isTemplate() ? t('documents.send.savingTemplate') : t('documents.send.savingDraft')}
                                    </span>
                                </div>
                            )}
                            
                            {lastSavedTime && !isSaving && !isLimitedEdit && autoSaveEnabled && !isTemplate() && (
                                <div className="document-compose-draft-status-indicator saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('documents.send.draftSavedAutoSave', { time: displayTimestamp })}
                                    </span>
                                </div>
                            )}
                            
                            {lastSavedTime && !isSaving && !isLimitedEdit && isTemplate() && (
                                <div className="document-compose-draft-status-indicator saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('documents.send.templateSaved', { time: displayTimestamp })}
                                    </span>
                                </div>
                            )}
                            
                            {lastSavedTime && !isSaving && !isLimitedEdit && !autoSaveEnabled && !isTemplate() && (
                                <div className="document-compose-draft-status-indicator saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('documents.send.draftSavedWithTime', { time: displayTimestamp })}
                                    </span>
                                </div>
                            )}
                            
                            {!lastSavedTime && !isSaving && !loadingDraft && !isLimitedEdit && !isTemplate() && (
                                <div className="document-compose-draft-status-indicator not-saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('documents.send.draftNotSaved')}
                                    </span>
                                </div>
                            )}
                            
                            {!lastSavedTime && !isSaving && !loadingDraft && !isLimitedEdit && isTemplate() && (
                                <div className="document-compose-draft-status-indicator not-saved">
                                    <SaveOutlined /> 
                                    <span>
                                        {t('documents.send.templateNotSaved')}
                                    </span>
                                </div>
                            )}
                            
                            {isLimitedEdit && (
                                <div className="document-compose-draft-status-indicator limited">
                                    <InfoCircleOutlined />
                                    <span>{t('messages.send.titleEditLimited')}</span>
                                </div>
                            )}
                        </div>
                        <div className="document-compose-header-center">
                            {!loadingDraft && !isLimitedEdit && templates.length > 0 && (
                                <Select
                                    placeholder={t('documents.send.selectTemplate') || 'Select a template'}
                                    loading={templatesLoading}
                                    allowClear
                                    className="document-compose-header-template-select"
                                    options={templates.map(tpl => ({
                                        value: tpl.access_link,
                                        label: tpl.subject || t('documents.send.unnamedTemplate') || 'Unnamed template'
                                    }))}
                                    onSelect={(accessLink) => {
                                        window.location.href = accessLink;
                                    }}
                                />
                            )}
                        </div>
                        <div className="document-compose-header-actions">
                            <Space>
                                {!isLimitedEdit && !isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleSaveAsTemplate}
                                    >
                                        {t('documents.send.saveAsTemplate')}
                                    </Button>
                                )}
                                {!isLimitedEdit && isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleUpdateTemplate}
                                        loading={isSaving}
                                        disabled={!draftId && autoSaveEnabled && lastSavedTime && !formModifiedRef.current}
                                    >
                                        {t('documents.send.updateTemplate')}
                                    </Button>
                                )}
                                {!isLimitedEdit && isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleSaveAsDraftFromTemplate}
                                    >
                                        {t('documents.send.saveAsDraft')}
                                    </Button>
                                )}
                                {!isLimitedEdit && !isTemplate() && (
                                    <Button 
                                        type="default" 
                                        className="btn-save"
                                        onClick={handleSaveAsDraft}
                                        loading={isSaving}
                                        disabled={!draftId && autoSaveEnabled && lastSavedTime && !formModifiedRef.current}
                                    >
                                        {draftId && autoSaveEnabled ? t('documents.send.updateDraft') : t('documents.send.saveAsDraft')}
                                    </Button>
                                )}
                                <Button 
                                    type="primary" 
                                    loading={submitLoading}
                                    htmlType="submit"
                                    form="document-compose-form"
                                >
                                    {isLimitedEdit 
                                        ? t('messages.send.updateMessage') 
                                        : (hasRecipients ? t('messages.send.sendMessage') : t('messages.send.createLink'))
                                    }
                                </Button>
                                <Tooltip title={t('messages.send.cancel')}>
                                    <Button 
                                        type="default" 
                                        className="btn-cancel"
                                        icon={<CloseOutlined />}
                                        onClick={handleCancel}
                                    />
                                </Tooltip>
                            </Space>
                        </div>
                </div>
            </div>
            {/* Detail + Body section - matches MessagePreview / TTLSend */}
            <div className="document-compose-detail-body-wrapper">
                <Form
                    id="document-compose-form"
                    form={form}
                    onFinish={handleSubmit}
                    layout="vertical"
                    preserve={true}
                    requiredMark={(label, { required }) => required ? <>{label} <span className="document-compose-required-asterisk">*</span></> : label}
                    onValuesChange={() => {
                        if (!initialLoadRef.current) {
                            formModifiedRef.current = true;
                            console.log('Form values changed, triggering auto-save. Auto-save enabled:', autoSaveEnabled);
                            triggerAutoSave();
                        }
                    }}
                >
                    {/* Top Section - Form Fields (structure matches MessageCompose) */}
                    <div className={`document-compose-form-top ${formTopCollapsed ? 'collapsed' : ''}`}>
                        {/* Row 1: Receivers (full width, no label) */}
                        <div className="document-compose-receivers-row">
                            <div className="document-compose-receivers-section">
                                <Form.Item
                                    name="recipient"
                                    preserve={true}
                                >
                                    <div className="document-compose-receiver-input-row">
                                        <div className="document-compose-recipient-field">
                                            <Select
                                                mode="tags"
                                                className="document-compose-input"
                                                placeholder={t('documents.send.recipientOptionalPlaceholder')}
                                                tokenSeparators={[',', ';', ' ']}
                                                allowClear
                                                showSearch
                                                open={recipientDropdownOpen}
                                                value={recipientTags}
                                                searchValue={recipientSearchText}
                                                autoClearSearchValue={false}
                                                listHeight={200}
                                                notFoundContent={null}
                                                suffixIcon={null}
                                                disabled={isLimitedEdit}
                                                onKeyDown={(e) => {
                                                    if ((e.key === 'Tab' || e.key === ';' || e.key === ' ') && recipientSearchText && recipientSearchText.trim() !== '') {
                                                        e.preventDefault();
                                                        const trimmedEmail = recipientSearchText.trim();
                                                        if (validateEmail(trimmedEmail)) {
                                                            const newTags = [...recipientTags, trimmedEmail];
                                                            setRecipientTags(newTags);
                                                            form.setFieldsValue({ recipient: newTags });
                                                            setRecipientSearchText('');
                                                            setHasRecipients(true);
                                                        } else if (trimmedEmail.includes('@')) {
                                                            message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                        }
                                                    }
                                                }}
                                                onFocus={() => setRecipientDropdownOpen(true)}
                                                onDropdownVisibleChange={(open) => setRecipientDropdownOpen(open)}
                                                onSearch={(text) => setRecipientSearchText(text)}
                                                dropdownRender={() => (
                                                    <ContactsDropdown
                                                        contacts={filteredRecipientContacts}
                                                        selectedTags={recipientTags}
                                                        field="recipient"
                                                        onSelect={handleContactSelect}
                                                        onAddFavorite={handleContactAddFavorite}
                                                        onRemoveFavorite={handleContactRemoveFavorite}
                                                        onEdit={handleContactEdit}
                                                        onDelete={handleContactDelete}
                                                        isLoading={contactsLoading}
                                                        t={t}
                                                    />
                                                )}
                                                onChange={(value) => {
                                                    if (selectingContact) return;
                                                    if (JSON.stringify(value) === JSON.stringify(recipientTags)) return;
                                                    const filteredValue = value.filter(item => {
                                                        const trimmedItem = typeof item === 'string' ? item.trim() : '';
                                                        const trimmedSearchText = recipientSearchText ? recipientSearchText.trim() : '';
                                                        if (trimmedItem === trimmedSearchText && !validateEmail(trimmedItem)) return false;
                                                        return true;
                                                    });
                                                    if (filteredValue.length > recipientTags.length) {
                                                        const newItem = filteredValue[filteredValue.length - 1];
                                                        if (newItem && typeof newItem === 'string') {
                                                            const trimmedEmail = newItem.trim();
                                                            if (trimmedEmail === '') {
                                                                const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                                form.setFieldsValue({ recipient: cleanedValues });
                                                                setRecipientTags(cleanedValues);
                                                                return;
                                                            }
                                                            const isValid = validateEmail(trimmedEmail);
                                                            if (!isValid) {
                                                                if (trimmedEmail.includes('@')) {
                                                                    message.error(t('messages.validation.invalidEmail', { email: trimmedEmail }));
                                                                } else if (trimmedEmail.length > 3 || trimmedEmail.includes('.')) {
                                                                    message.error(t('messages.validation.missingAtSymbol', { email: trimmedEmail }));
                                                                }
                                                                const cleanedValues = filteredValue.filter(email => email !== newItem);
                                                                form.setFieldsValue({ recipient: cleanedValues });
                                                                setRecipientTags(cleanedValues);
                                                                return;
                                                            } else {
                                                                setRecipientSearchText('');
                                                            }
                                                        }
                                                    }
                                                    const cleanedValues = filteredValue.map(email => 
                                                        typeof email === 'string' ? email.trim() : email
                                                    ).filter(email => email !== '');
                                                    form.setFieldsValue({ recipient: cleanedValues });
                                                    setRecipientTags(cleanedValues);
                                                    setHasRecipients(cleanedValues.length > 0);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </Form.Item>
                            </div>
                        </div>

                        {/* Row 2: Access Code (50%) | Expiry (50%) */}
                        <div className="document-compose-options-row">
                            <div className="document-compose-access-code-section" style={{ width: '100%' }}>
                                <Button
                                    type="default"
                                    icon={<LockOutlined />}
                                    onClick={() => setAccessCodeDeliveryModalOpen(true)}
                                    disabled={isLimitedEdit}
                                    className={accessCodeBtnError ? 'access-code-btn-error' : ''}
                                    style={{ width: '100%', height: 40 }}
                                >
                                    {t('compose.accessCodeDelivery.done', 'Access Code')}
                                </Button>
                            </div>
                            <Tooltip title={t('messages.send.expiryTooltip', { 
                                days: userSettings?.message_security?.message_lifetime_days || t('messages.send.loadingDraft'),
                                time: userSettings?.message_security?.daily_expiration_time || t('messages.send.loadingDraft')
                            })}>
                                <div className="document-compose-expiry-section document-compose-expiry-inline">
                                    <Form.Item
                                        name="timestamp"
                                        rules={[{ required: true, message: t('messages.validation.expiryRequired') }]}
                                        style={{ marginBottom: 0 }}
                                    >
                                        <DatePicker
                                            showTime
                                            showNow={false}
                                            format="YYYY-MM-DD HH:mm:ss"
                                            style={{ width: '100%' }}
                                            disabledDate={(current) => current && current < dayjs().startOf('day')}
                                            placeholder={t('messages.send.expiryPlaceholder')}
                                        />
                                    </Form.Item>
                                </div>
                            </Tooltip>
                        </div>

                        {/* Row 3: Subject (full width, no label) */}
                        <div className="document-compose-subject-row">
                            <Form.Item
                                name="subject"
                                rules={[{ required: true, message: t('messages.validation.subjectRequired') }]}
                                validateTrigger={['onSubmit']}
                            >
                                <Input placeholder={t('messages.send.subjectPlaceholder')} disabled={isLimitedEdit} />
                            </Form.Item>
                        </div>
                    </div>

                    {/* Validation error (centered) + Toggles (right) - matches MessageCompose */}
                    <Form.Item noStyle shouldUpdate>
                        {() => {
                            const subjectError = form.getFieldError('subject')?.[0] || '';
                            const messageError = form.getFieldError('message')?.[0] || '';
                            const errorText = subjectError || messageError || documentRequestsError || invalidDocumentRequestError || '';
                            return (
                                <div className="document-compose-validation-error-row">
                                    <div className="document-compose-validation-error">
                                        {errorText}
                                    </div>
                                    <div className="document-compose-validation-error-toggles">
                                        <Tooltip title={t('messages.send.selfDestructTooltip')}>
                                            <div className="document-compose-toggle-card document-compose-toggle-inline">
                                                <Form.Item name="self_destruct" valuePropName="checked" style={{ marginBottom: 0 }}>
                                                    <Switch
                                                        checkedChildren={t('messages.send.selfDestructLabel')}
                                                        unCheckedChildren={t('messages.send.selfDestructLabel')}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </Tooltip>
                                        <Tooltip title={t('messages.send.preventCopyTooltip')}>
                                            <div className="document-compose-toggle-card document-compose-toggle-inline">
                                                <Switch
                                                    checked={preventCopyEnabled}
                                                    onChange={setPreventCopyEnabled}
                                                    checkedChildren={t('messages.send.preventCopyLabel')}
                                                    unCheckedChildren={t('messages.send.preventCopyLabel')}
                                                    disabled={isLimitedEdit}
                                                />
                                            </div>
                                        </Tooltip>
                                    </div>
                                </div>
                            );
                        }}
                    </Form.Item>

                    {/* Main Content - Unified Container: Message | Requested Docs */}
                    <div className="document-compose-main-content" ref={mainContentRef}>
                        {/* Column 1: Message Editor */}
                        <div 
                            className="document-compose-editor-section"
                            style={{ flex: `0 1 ${columnWidths.editor}%`, minWidth: 0 }}
                        >
                            <Form.Item
                                name="message"
                                rules={[{
                                    validator: (_, value) => {
                                        const stripped = (value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
                                        if (!stripped) {
                                            return Promise.reject(new Error(t('messages.validation.messageRequired')));
                                        }
                                        return Promise.resolve();
                                    }
                                }]}
                                validateTrigger={['onSubmit']}
                                className="message-editor-form-item"
                            >
                                <MessageEditor
                                    placeholder={t('documents.send.messageNotesPlaceholder')}
                                    disabled={isLimitedEdit}
                                    formCollapsed={formTopCollapsed}
                                    onFormCollapseToggle={() => setFormTopCollapsed(!formTopCollapsed)}
                                />
                            </Form.Item>
                        </div>

                        {/* Resize Handle: Between Editor and Requests */}
                        <div 
                            className="document-compose-column-resize-handle"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                setResizingColumn('editor');
                            }}
                        />

                        {/* Column 2: Document Requests + Embedded Templates (toggleable) */}
                        <div 
                            className={`document-compose-document-requests ${!templatesCollapsed && !isLimitedEdit ? 'with-templates-panel' : ''}`}
                            style={{ flex: `0 1 ${columnWidths.requests}%`, minWidth: 280 }}
                        >
                            <div className="document-compose-section-header">
                                <span className="document-compose-section-title">{t('documents.send.documentRequestsTitle')}</span>
                                <div className="document-compose-requests-header-actions">
                                    {!isLimitedEdit && (
                                        <>
                                            <Tooltip title={templatesCollapsed ? t('documents.send.showTemplates') : t('documents.send.hideTemplates')}>
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    icon={templatesCollapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                                                    onClick={() => setTemplatesCollapsed(!templatesCollapsed)}
                                                    className="document-compose-templates-toggle-btn"
                                                    aria-label={templatesCollapsed ? 'Show templates' : 'Hide templates'}
                                                />
                                            </Tooltip>
                                            <Button
                                                type="primary"
                                                onClick={handleDocumentRequestClick}
                                                className="document-request-button-new"
                                            >
                                                +
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                            
                            <div className="document-compose-requests-body" ref={requestsBodyRef}>
                                <div 
                                    className={`document-compose-requests-list-wrapper ${isDraggingTemplate ? 'template-drop-target' : ''}`}
                                    style={!templatesCollapsed && !isLimitedEdit ? { flex: `0 0 calc(${100 - templatesPanelWidth}% - 6px)`, minWidth: 0 } : undefined}
                                    onDragOver={handleRequestsDragOver}
                                    onDrop={handleRequestsDrop}
                                >
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="requests" isDropDisabled={isLimitedEdit}>
                                    {(provided, snapshot) => (
                                        <div 
                                            className={`document-requests-list ${snapshot.isDraggingOver ? 'dragging-over' : ''}`}
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            {documentRequests.length > 0 ? (
                                                documentRequests.map((request, index) => (
                                                    <Draggable 
                                                        key={request.id} 
                                                        draggableId={`request-${request.id}`}
                                                        index={index}
                                                        isDragDisabled={isLimitedEdit}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <div 
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                {...provided.dragHandleProps}
                                                                className={`document-request-item ${snapshot.isDragging ? 'dragging' : ''} ${isLimitedEdit ? 'read-only' : ''} ${invalidDocumentRequestIds.has(request.id) ? 'validation-error' : ''}`}
                                                            >
                                                                <div className="document-request-drag-handle" aria-hidden="true">⋮⋮</div>
                                                                {!isLimitedEdit && (
                                                                    <Button
                                                                        type="text"
                                                                        size="small"
                                                                        icon={<CloseOutlined />}
                                                                        onClick={(e) => { e.stopPropagation(); handleRemoveDocumentRequest(request.id); }}
                                                                        className="document-request-remove"
                                                                        title={t('documents.send.removeDocumentRequest')}
                                                                    />
                                                                )}
                                                                <div className="document-request-content">
                                                                    <div className="document-request-text">
                                                                        <Input
                                                                            value={request.title || ''}
                                                                            onChange={(e) => handleDocumentRequestFieldChange(request.id, 'title', e.target.value)}
                                                                            placeholder={t('documents.send.enterDocumentTitle')}
                                                                            className="document-request-inline-edit-title"
                                                                            maxLength={250}
                                                                            bordered={false}
                                                                            disabled={isLimitedEdit}
                                                                        />
                                                                        
                                                                        <Input.TextArea
                                                                            value={request.description || ''}
                                                                            onChange={(e) => handleDocumentRequestFieldChange(request.id, 'description', e.target.value)}
                                                                            placeholder={t('documents.send.enterDocumentDescription')}
                                                                            className="document-request-inline-edit-description"
                                                                            rows={1}
                                                                            maxLength={1000}
                                                                            bordered={false}
                                                                            autoSize={{ minRows: 1, maxRows: 1 }}
                                                                            disabled={isLimitedEdit}
                                                                        />
                                                                        
                                                                        <Select
                                                                            mode="multiple"
                                                                            value={request.return_type || ['*']}
                                                                            onChange={(value) => handleDocumentRequestFieldChange(request.id, 'return_type', value)}
                                                                            placeholder={t('documents.send.chooseAcceptedFileTypes')}
                                                                            className="document-request-inline-edit-return-type"
                                                                            popupClassName="document-request-file-type-dropdown"
                                                                            listHeight={600}
                                                                            size="small"
                                                                            bordered={false}
                                                                            disabled={isLimitedEdit}
                                                                            open={openFileTypeDropdownId === request.id}
                                                                            onDropdownVisibleChange={(open) => setOpenFileTypeDropdownId(open ? request.id : null)}
                                                                            onBlur={() => setOpenFileTypeDropdownId(null)}
                                                                            dropdownRender={(menu) => (
                                                                                <div onMouseLeave={() => setOpenFileTypeDropdownId(null)}>
                                                                                    {menu}
                                                                                </div>
                                                                            )}
                                                                        >
                                                                            <Select.Option value="*">{t('documents.send.anyFileType')}</Select.Option>
                                                                            {settings?.allowedFileTypes && 
                                                                                settings.allowedFileTypes.split(',').map(type => (
                                                                                    <Select.Option key={type.trim()} value={type.trim()}>
                                                                                        {type.trim()}
                                                                                    </Select.Option>
                                                                                ))
                                                                            }
                                                                        </Select>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                ))
                                            ) : (
                                                <div 
                                                    className={`document-requests-empty-placeholder ${!isLimitedEdit ? 'clickable' : ''}`}
                                                    onClick={!isLimitedEdit ? handleDocumentRequestClick : undefined}
                                                    role={!isLimitedEdit ? 'button' : undefined}
                                                    tabIndex={!isLimitedEdit ? 0 : undefined}
                                                    onKeyDown={!isLimitedEdit ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleDocumentRequestClick(); } } : undefined}
                                                >
                                                    <CloudUploadOutlined className="document-requests-empty-icon" />
                                                    <span className="document-requests-empty-label">
                                                        {t('documents.send.dropTemplateOrClickAdd')}
                                                    </span>
                                                </div>
                                            )}
                                            {provided.placeholder}
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                                </div>

                                {/* Resize handle between requests list and templates panel */}
                                {!isLimitedEdit && !templatesCollapsed && (
                                    <div
                                        className="document-compose-templates-resize-handle"
                                        onMouseDown={(e) => {
                                            e.preventDefault();
                                            setIsResizingTemplates(true);
                                        }}
                                    />
                                )}

                                {/* Embedded Templates Panel (toggleable, like MessageEditor attachments) */}
                                {!isLimitedEdit && !templatesCollapsed && (
                                    <div 
                                        className="document-compose-templates-panel"
                                        style={{ flex: `0 0 ${templatesPanelWidth}%` }}
                                    >
                                        <div className="document-compose-templates-panel-header">
                                            <span className="document-compose-templates-panel-title">{t('documents.send.recentTemplatesHeader')}</span>
                                            <div className="document-recents-sort-toggle" onClick={handleSortToggle}>
                                                <span className="sort-label">
                                                    {templateSortType === 'recent' ? t('documents.send.sortMostRecent') : t('documents.send.sortMostUsed')}
                                                </span>
                                                <span className="template-count">
                                                    ({filteredTemplates.length}/{documentRecents.length})
                                                </span>
                                            </div>
                                        </div>
                                        <div className="document-recents-search">
                                            <Input
                                                placeholder={t('documents.send.searchTemplates')}
                                                value={templateSearchText}
                                                onChange={(e) => setTemplateSearchText(e.target.value)}
                                                className="template-search-input"
                                                size="small"
                                                allowClear
                                            />
                                        </div>
                                        {documentRecentsLoading ? (
                                            <div className="document-recents-loading">{t('documents.send.loadingTemplates')}</div>
                                        ) : filteredTemplates.length > 0 ? (
                                            <div className="document-recents-list">
                                                {filteredTemplates.map((recent) => (
                                                    <div 
                                                        key={recent.id} 
                                                        className="document-recent-item"
                                                        draggable={!isLimitedEdit}
                                                        onDragStart={(e) => handleTemplateDragStart(e, recent)}
                                                        onDragEnd={handleTemplateDragEnd}
                                                    >
                                                        <FileOutlined className="document-recent-icon" />
                                                        <div className="document-recent-content">
                                                            <div className="document-recent-text">
                                                                <span className="document-recent-title">
                                                                    {recent.title || t('documents.send.untitledTemplate')}
                                                                </span>
                                                                <span className="document-recent-description">
                                                                    {recent.description || t('documents.send.noDescription')}
                                                                </span>
                                                                <div className="document-recent-meta">
                                                                    <span className="document-recent-return-type">
                                                                        {recent.return_type || '*'}
                                                                    </span>
                                                                    <span className="document-recent-usage">
                                                                        {t('documents.send.usedCount', { 
                                                                            count: recent.numberused, 
                                                                            time: recent.numberused === 1 ? t('documents.send.usedTime') : t('documents.send.usedTimes') 
                                                                        })}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <div className="document-recent-actions">
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<PlusOutlined />}
                                                                onClick={() => handleUseDocumentRecent(recent)}
                                                                className="document-recent-use"
                                                                title={t('documents.send.useThisTemplate')}
                                                            />
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<CloseOutlined />}
                                                                onClick={() => handleDeleteDocumentRecent(recent.id)}
                                                                className="document-recent-remove"
                                                                title={t('documents.send.deleteTemplate')}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : documentRecents.length > 0 ? (
                                            <div className="document-recents-empty">
                                                <FileOutlined />
                                                <span>{t('documents.send.noTemplatesMatch')}</span>
                                            </div>
                                        ) : (
                                            <div className="document-recents-empty">
                                                <FileOutlined />
                                                <span>{t('documents.send.noRecentTemplates')}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                    </div>
                </Form>
            </div>
        </div>

        <AccessCodeDeliveryModal
            open={accessCodeDeliveryModalOpen}
            onClose={() => setAccessCodeDeliveryModalOpen(false)}
            onSave={handleAccessCodeModalSave}
            recipients={{ to: recipientTags, cc: [], bcc: [] }}
            accessCodeDelivery={accessCodeDelivery}
            onAccessCodeDeliveryChange={setAccessCodeDelivery}
            accessCodeSmsPhones={accessCodeSmsPhones}
            onAccessCodeSmsPhonesChange={setAccessCodeSmsPhones}
            contacts={contacts}
        />

        <ContactGroupModal
            visible={contactEditModalVisible}
            onClose={handleContactEditModalClose}
            onAdded={handleContactEditModalSaved}
            type={contactEditingContact?._type || 'contact'}
            availableContacts={contacts}
            editingContact={contactEditingContact}
        />
        </>
    );
};

export default DocumentCompose;